import { useCallback, useRef, useState } from 'react';
import { apiClient } from '@/lib/api';

/**
 * Chained-save state machine for the Add-Aircraft-Manually wizard.
 * Handles BOTH create and edit modes — mode + optional `editing` block
 * are passed in with the payload; step routing branches internally.
 *
 * Steps:
 *   1  aircraft_make_models       (create) POST if new/dirty; reuse selected id otherwise
 *                                 (edit)   Reuse / PATCH (dirty + sameSession) / POST new (dirty + older)
 *   2  organizations/aircrafts    (create) POST → captures aircraftId
 *                                 (edit)   PATCH /organizations/:org/aircrafts/:id
 *   3  aircrafts/{id}/features    (create) POST
 *                                 (edit)   POST if !editing.hadFeatures, PATCH otherwise
 *   4  aircrafts/{id}/amenities   (create) POST
 *                                 (edit)   POST if !editing.hadAmenities, PATCH otherwise
 *   5  S3 image uploads           New picks only; kind:'existing' rows pass through.
 *                                 Key layout: photos/{org_id}/{TAIL_FOLDER}/{stamp}-{i}.{ext}
 *                                 (org_id is part of the key so a later delete-guard can
 *                                 tell whose photos are safe to remove.)
 *   6  organizations/aircrafts    PATCH with final image_urls + banner_image_url
 *   7  S3 cleanup (edit only)     Diff editing.originalImageUrls \ final image_urls;
 *                                 for each removed URL matching /photos/{our-org-id}/…
 *                                 fire the delete. Delete calls are currently
 *                                 STUBBED — TODO(BE)-S3-DELETE — until the BE
 *                                 delete-by-key endpoint ships. Errors here are
 *                                 best-effort — they DO NOT trigger the retry
 *                                 dialog (the aircraft save already succeeded).
 *
 * Cross-step continuation state is stored in progressRef so a retry
 * skips whatever succeeded before the failure and resumes from the
 * failed step.
 */

/**
 * Same 5-min window the wizard uses to decide whether the aircraft +
 * its make/model row were created in the same wizard session. Kept
 * exported so the payload builder can compute the flag itself.
 */
export const SAME_SESSION_MS = 5 * 60 * 1000;
export const isSameSessionMakeModel = (aircraftCreatedAt, makeModelCreatedAt) => {
  if (!aircraftCreatedAt || !makeModelCreatedAt) return false;
  const a = new Date(aircraftCreatedAt).getTime();
  const b = new Date(makeModelCreatedAt).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return false;
  return Math.abs(a - b) <= SAME_SESSION_MS;
};

/**
 * Only URLs that clearly live under this org's photos/ folder are
 * safe to delete — matches new-format keys `photos/{org_id}/…`.
 * Pre-migration keys (`photos/{TAIL}/…`, no org segment) fail this
 * check by design — we never delete what we can't attribute.
 */
export const isOurOrgS3Photo = (url, orgId) => {
  if (!url || !orgId) return false;
  return url.includes(`/photos/${orgId}/`);
};

export const useAddAircraftSaveMachine = ({ organizationId }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState(null);        // { step, message, url, body, response }
  const [success, setSuccess] = useState(null);    // final aircraft record returned by step 6

  const progressRef = useRef({
    makeModelId: null,
    aircraftId: null,
    featuresDone: false,
    amenitiesDone: false,
    uploadedImageUrls: null,
    finalAircraft: null,
    cleanupDone: false,      // step 7 (edit mode only)
  });
  const lastPayloadRef = useRef(null);

  const extFromMime = (type) =>
    type === 'image/png' ? 'png'
    : type === 'image/jpeg' || type === 'image/jpg' ? 'jpg'
    : (String(type || '').split('/')[1] || 'jpg');

  const resetProgress = () => {
    progressRef.current = {
      makeModelId: null,
      aircraftId: null,
      featuresDone: false,
      amenitiesDone: false,
      uploadedImageUrls: null,
      finalAircraft: null,
      cleanupDone: false,
    };
    setError(null);
    setSuccess(null);
  };

  /**
   * Run the chain. Payload shape:
   *   {
   *     mode: 'create' | 'edit',
   *     editing?: {
   *       aircraftId,
   *       hadFeatures: boolean,
   *       hadAmenities: boolean,
   *       originalImageUrls: string[],
   *     },
   *     makeModel: {
   *       id: string | null,        // existing aircraft_make_model_id (edit) or null (create)
   *       isDirty: boolean,         // form vs selectedRow snapshot
   *       sessionPatchOk: boolean,  // aircraft + make_model created within SAME_SESSION_MS
   *       body,
   *     },
   *     aircraft:  { body },        // step-2 body (image_urls omitted — set via step 6)
   *     features:  { body },        // aircraft_id auto-filled at run time
   *     amenities: { body },        // aircraft_id auto-filled at run time
   *     photos:    [{ kind, file?, previewUrl, saveUrl?, name, size }],
   *     tailNumber,
   *   }
   */
  const run = useCallback(async (payload) => {
    if (!organizationId) {
      setError({ step: 0, message: 'Organization is required', url: null, body: null, response: null });
      return;
    }
    lastPayloadRef.current = payload;
    setIsRunning(true);
    setError(null);

    const mode = payload?.mode || 'create';
    const editing = payload?.editing || null;

    const logSuccess = (step, result) => {
      // eslint-disable-next-line no-console
      console.log(`[add-aircraft:${mode}] step ${step} OK`, result);
    };
    const failStep = (step, url, body, response, err) => {
      const message = err?.message || response?.detail || 'Request failed.';
      // eslint-disable-next-line no-console
      console.error(`[add-aircraft:${mode}] step ${step} FAILED`, { url, body, response, error: err });
      setError({ step, message, url, body, response });
      setIsRunning(false);
    };

    // Edit mode seeds progressRef.aircraftId so step 2 uses PATCH.
    if (mode === 'edit' && editing?.aircraftId && !progressRef.current.aircraftId) {
      progressRef.current.aircraftId = editing.aircraftId;
    }

    // ── Step 1: aircraft_make_models ─────────────────────────────────
    if (!progressRef.current.makeModelId) {
      const mm = payload.makeModel;
      if (!mm.isDirty && mm.id) {
        progressRef.current.makeModelId = mm.id;
        logSuccess(1, { id: mm.id, response: '(reused existing row — unchanged)' });
      } else if (mode === 'edit' && mm.isDirty && mm.sessionPatchOk && mm.id) {
        // Dirty AND created in the same wizard session as the aircraft
        // → safe to mutate in place (no other aircraft is likely to
        // reference this row yet).
        try {
          const patched = await apiClient.updateAircraftMakeModel(mm.id, mm.body);
          progressRef.current.makeModelId = mm.id;
          logSuccess(1, { id: mm.id, response: patched });
        } catch (err) {
          failStep(1, `/aircraft-make-models/${mm.id}`, mm.body, err?.response || null, err);
          return;
        }
      } else {
        // Dirty + old (or create mode + isNew) → POST new row.
        try {
          const created = await apiClient.createAircraftMakeModel(mm.body);
          progressRef.current.makeModelId = created?.aircraft_make_model_id || created?.id || null;
          logSuccess(1, { id: progressRef.current.makeModelId, response: created });
        } catch (err) {
          failStep(1, '/aircraft-make-models', mm.body, err?.response || null, err);
          return;
        }
      }
    }

    // ── Step 2: aircraft (POST create / PATCH edit) ──────────────────
    const step2Done = mode === 'edit'
      ? (progressRef.current.aircraftId && progressRef.current.finalAircraft)
      : !!progressRef.current.aircraftId;

    if (mode === 'edit') {
      // In edit mode step 2 is PATCH — we don't have a completion flag
      // separate from "finalAircraft" (step 6 PATCH), so we always run
      // it once per fresh run. Once finalAircraft is set (step 6),
      // both are considered done.
      if (!progressRef.current.step2EditDone) {
        const body = { ...payload.aircraft.body, aircraft_make_model_id: progressRef.current.makeModelId };
        try {
          const patched = await apiClient.updateAircraftForOrganization(
            organizationId,
            progressRef.current.aircraftId,
            body,
          );
          progressRef.current.step2EditDone = true;
          logSuccess(2, { id: progressRef.current.aircraftId, response: patched });
        } catch (err) {
          failStep(2, `/organizations/${organizationId}/aircrafts/${progressRef.current.aircraftId}`, body, err?.response || null, err);
          return;
        }
      }
    } else if (!step2Done) {
      const body = { ...payload.aircraft.body, aircraft_make_model_id: progressRef.current.makeModelId };
      try {
        const created = await apiClient.createAircraftForOrganization(organizationId, body);
        progressRef.current.aircraftId = created?.aircraft_id || created?.id || null;
        logSuccess(2, { id: progressRef.current.aircraftId, response: created });
      } catch (err) {
        failStep(2, `/organizations/${organizationId}/aircrafts`, body, err?.response || null, err);
        return;
      }
    }

    // ── Step 3: features (POST create/first-time / PATCH edit) ───────
    if (!progressRef.current.featuresDone) {
      const body = { ...payload.features.body, aircraft_id: progressRef.current.aircraftId };
      const useUpdate = mode === 'edit' && !!editing?.hadFeatures;
      try {
        const result = useUpdate
          ? await apiClient.updateAircraftFeatures(progressRef.current.aircraftId, body)
          : await apiClient.createAircraftFeatures(progressRef.current.aircraftId, body);
        progressRef.current.featuresDone = true;
        logSuccess(3, { response: result, verb: useUpdate ? 'PATCH' : 'POST' });
      } catch (err) {
        failStep(3, `/aircrafts/${progressRef.current.aircraftId}/features`, body, err?.response || null, err);
        return;
      }
    }

    // ── Step 4: amenities (POST create/first-time / PATCH edit) ──────
    if (!progressRef.current.amenitiesDone) {
      const body = { ...payload.amenities.body, aircraft_id: progressRef.current.aircraftId };
      const useUpdate = mode === 'edit' && !!editing?.hadAmenities;
      try {
        const result = useUpdate
          ? await apiClient.updateAircraftAmenities(progressRef.current.aircraftId, body)
          : await apiClient.createAircraftAmenities(progressRef.current.aircraftId, body);
        progressRef.current.amenitiesDone = true;
        logSuccess(4, { response: result, verb: useUpdate ? 'PATCH' : 'POST' });
      } catch (err) {
        failStep(4, `/aircrafts/${progressRef.current.aircraftId}/amenities`, body, err?.response || null, err);
        return;
      }
    }

    // ── Step 5: S3 uploads (new picks) + passthrough (existing) ──────
    if (!progressRef.current.uploadedImageUrls) {
      const items = Array.isArray(payload.photos) ? payload.photos : [];
      if (items.length === 0) {
        progressRef.current.uploadedImageUrls = [];
      } else {
        const uploadable = items.filter((p) => p.kind !== 'existing' && p.file);
        const tailFolder = String(payload.tailNumber || progressRef.current.aircraftId || 'unknown')
          .replace(/[^A-Z0-9]/gi, '')
          .toUpperCase() || 'UNKNOWN';
        const stamp = Date.now();
        try {
          const uploaded = await Promise.all(
            uploadable.map(async (p, i) => {
              const ext = extFromMime(p.file?.type);
              // New key layout — org_id embedded so a future delete
              // knows this belongs to us. Pre-migration keys lacking
              // org_id fail the isOurOrgS3Photo guard and are left alone.
              const key = `photos/${organizationId}/${tailFolder}/${stamp}-${i}.${ext}`;
              const s3Urls = await apiClient.getS3SignedUrl(key, p.file?.type);
              const realUrl = await apiClient.uploadFileToS3(p.file, s3Urls);
              return { previewUrl: p.previewUrl, realUrl };
            }),
          );
          const swap = new Map(uploaded.map(({ previewUrl, realUrl }) => [previewUrl, realUrl]));
          // For `existing` rows persist saveUrl (relative /images/… for
          // bundled suggestions, or an already-persisted S3 URL for
          // edit-mode originals); for new rows persist the real S3 URL.
          progressRef.current.uploadedImageUrls = items
            .map((p) => (
              p.kind === 'existing'
                ? (p.saveUrl || p.previewUrl)
                : swap.get(p.previewUrl)
            ))
            .filter(Boolean);
          uploadable.forEach((p) => p?.previewUrl && URL.revokeObjectURL(p.previewUrl));
          logSuccess(5, {
            uploaded: uploaded.length,
            passthrough: items.length - uploadable.length,
            urls: progressRef.current.uploadedImageUrls,
          });
        } catch (err) {
          failStep(5, 'S3 uploads', { photoCount: items.length }, null, err);
          return;
        }
      }
    }

    // ── Step 6: PATCH image_urls + banner_image_url ──────────────────
    if (!progressRef.current.finalAircraft) {
      const body = {
        image_urls: progressRef.current.uploadedImageUrls || [],
        banner_image_url: (progressRef.current.uploadedImageUrls || [])[0] || null,
      };
      try {
        const patched = await apiClient.updateAircraftForOrganization(
          organizationId,
          progressRef.current.aircraftId,
          body,
        );
        progressRef.current.finalAircraft = patched || {
          aircraft_id: progressRef.current.aircraftId,
          ...body,
        };
        logSuccess(6, { id: progressRef.current.aircraftId, response: patched });
      } catch (err) {
        failStep(6, `/organizations/${organizationId}/aircrafts/${progressRef.current.aircraftId}`, body, err?.response || null, err);
        return;
      }
    }

    // ── Step 7: S3 cleanup (edit mode only, best-effort) ─────────────
    // Diff editing.originalImageUrls \ final image_urls; for each URL
    // that was removed AND is provably ours, queue a delete. Failures
    // are logged but do NOT trigger the retry dialog — the aircraft
    // save (steps 1-6) already succeeded.
    if (mode === 'edit' && !progressRef.current.cleanupDone) {
      const original = Array.isArray(editing?.originalImageUrls) ? editing.originalImageUrls : [];
      const final = new Set(progressRef.current.uploadedImageUrls || []);
      const removed = original.filter((u) => !final.has(u));
      const toDelete = removed.filter((u) => isOurOrgS3Photo(u, organizationId));
      const skipped = removed.length - toDelete.length;
      if (toDelete.length > 0) {
        // eslint-disable-next-line no-console
        console.log(`[add-aircraft:edit] step 7 — ${toDelete.length} S3 delete(s) queued, ${skipped} skipped as unknown-owner`, toDelete);
        // TODO(BE)-S3-DELETE — uncomment the block below once the BE
        // delete-by-key endpoint (see api.js:deleteS3Object) ships.
        // await Promise.all(
        //   toDelete.map(async (url) => {
        //     const key = url.slice(url.indexOf('/photos/') + 1); // strip protocol + host
        //     try { await apiClient.deleteS3Object(key); }
        //     catch (err) { console.warn('[add-aircraft:edit] S3 delete failed (best-effort)', { key, err }); }
        //   }),
        // );
      } else if (skipped > 0) {
        // eslint-disable-next-line no-console
        console.log(`[add-aircraft:edit] step 7 — ${skipped} removed URL(s) skipped as unknown-owner (pre-migration or another org)`);
      }
      progressRef.current.cleanupDone = true;
    }

    setSuccess(progressRef.current.finalAircraft);
    setIsRunning(false);
  }, [organizationId]);

  const retry = useCallback(() => {
    if (!lastPayloadRef.current) return;
    setError(null);
    run(lastPayloadRef.current);
  }, [run]);

  const reset = useCallback(() => {
    resetProgress();
    lastPayloadRef.current = null;
    setIsRunning(false);
  }, []);

  return { run, retry, reset, isRunning, error, success, progressRef };
};
